import { Component, DetailEvent, Entity, registerComponent } from "aframe";
import { Quaternion, Vector3 } from "three";
import { AmmoBodyComponent } from "./ammo";
import { AnchorEntity } from "./anchor";
import { SlotEntity, SlotEventDetail } from "./slot";
import { TemplateComponent } from "./template";
import { waitForLoaded } from "./utils";
import { propagateVariablesChange, VariablesEntity } from "./variables";


interface SlottableComponent extends Component
{
	attachListener : EventListener;
	detachListener : EventListener;
	parentChangedListener : EventListener;
}

// Slottable component reacts to "attach-to-slot" and "detach-from-slot" events by attaching or detaching its Entity
registerComponent("slottable", {

	init(this : SlottableComponent )
	{
		const el = this.el;

		this.attachListener = ((event : DetailEvent<SlotEntity>) =>
		{
			event.stopPropagation();
			event.preventDefault();

			let slotEl : SlotEntity | null = event.detail;
			let anchorEl : AnchorEntity | null = event.target as AnchorEntity;

			if(anchorEl.components.anchor && anchorEl.components.anchor.fits(slotEl))
			{
				const slotData = slotEl.components.slot.data;
				const anchorData = anchorEl.components.anchor.data;
				if(slotData.anchor)
					return; //Slot is already filled
				if(slotData.locked && anchorData.attach)
					return; //Slot can't be attached to
			}
			else //Loose "attach-to" : find a suitable [anchor]/[slot] pair
			{
				//If the event is dispatched on an [anchor], attach that [anchor] only
				const anchors : AnchorEntity[] = [];
				if(anchorEl.components.anchor)
					anchors.push(anchorEl);
				else
				{
					//Try to attach to any child [anchor] that's not a descendant of another [slottable]
					const subAnchors = Array.from( anchorEl.querySelectorAll<AnchorEntity>(":scope [anchor]") );
					const subSlottableAnchors = Array.from( anchorEl.querySelectorAll<AnchorEntity>(":scope [slottable] [anchor], :scope [slottable][anchor]") );
					for(let a of subAnchors)
						subSlottableAnchors.includes(a) || anchors.push(a);
				}


				const slots = Array.from(slotEl.querySelectorAll<SlotEntity>(":scope [slot]"));
				//If the event detail is a slot, try this slot first
				if(slotEl.components.slot) slots.unshift(slotEl);

				
				slotEl = null;
				anchorEl = null;
				for(let a of anchors)
				{
					const anchorData = a.components.anchor.data;
					for(let s of slots)
					{
						if(!a.components.anchor.fits(s))
							continue;

						const slotData = s.components.slot.data;
						if(slotData.anchor)
						 	continue; //Slot is not empty

						
						if(slotData.locked && anchorData.attach)
							continue; //Slot is locked and can't be attached to

						slotEl = s;
						anchorEl = a;
						break;
					}

					if(slotEl && anchorEl) //Found an empty slot!
						break;
				}

				if(!(slotEl && anchorEl))
				{
					for(let key in event.detail.components)
					{
						const cmp = event.detail.components[key];
						if( (cmp.name == "template") && !(cmp as TemplateComponent).templateLoaded() )
						{
							return event.detail.addEventListener("template-loaded", 
								() => this.attachListener(event),
								{ once : true } );
						}
					}


					console.warn("Cannot attach to slot");
					console.dir(event);
					return;
				}
			}



			if( anchorEl.components.anchor.data.attach ) // A parenting anchor
			{
				const currentSlotData = slotEl.components.slot.data;
				if( (currentSlotData.anchor === anchorEl) && (currentSlotData.item === el) )
					return; //Already there!

				if(currentSlotData.anchor) //There's something in our new slot, try to remove it properly : 
					currentSlotData.anchor.emit("detach-from-slot", slotEl);

				// The [slot] will handle the parenting and event dispatching.
				slotEl.setAttribute("slot", { item : el, anchor : anchorEl });
			}
			else // An aligning anchor
			{
				slotEl.object3D.updateWorldMatrix(true, false);
				const slotM = slotEl.object3D.matrixWorld;

				anchorEl.components.anchor.setSlotPosition(el);
				el.object3D.position.applyMatrix4(slotM);
				
				const rot = new Quaternion();
				slotM.decompose( {} as Vector3, rot, {} as Vector3 );
				//rot.setFromRotationMatrix(slotM);
				el.object3D.quaternion.premultiply(rot);
				el.object3D.updateWorldMatrix(true, true);


				const ammoBody = el.components["ammo-body"] as AmmoBodyComponent;
				if(ammoBody) // Synchronize physics :
				{
					const pos = new Vector3(), scl = new Vector3();
					el.object3D.matrixWorld.decompose(pos, rot, scl);
					ammoBody.moveTo(pos, rot);
				}

				const detail : SlotEventDetail = {
					slot : slotEl,
					anchor : anchorEl,
					item : el
				};
				anchorEl.emit("aligned-to-slot", detail);
				slotEl.emit("item-aligned", detail);
			}
			
		}) as EventListener;


		const attachToNaturalSlot = async () =>
		{
			//Is natural parent a Slot entity ?
			const naturalSlot = this.el.parentElement as SlotEntity;
			if(!(naturalSlot && naturalSlot.components.slot))
				return;

			await waitForLoaded(naturalSlot);

			const allMyAnchors = Array.from(this.el.querySelectorAll<AnchorEntity>("[anchor]"));
			if(this.el.matches("[anchor]"))
				allMyAnchors.unshift(this.el as AnchorEntity);

			const myAnchors = (await Promise.all(allMyAnchors.map(waitForLoaded))).filter((ae) => (ae as AnchorEntity).components.anchor.fits(naturalSlot));
			if(!myAnchors.length)
				return;

			//This the best anchor that fits the slot
			let myAnchor = myAnchors.pop();
			if(myAnchors.length) //More than 1 : find the closest one
			{
				let distance = naturalSlot.components.slot.sqrDistanceFrom(myAnchor!);
				for(let anchor of myAnchors)
				{
					const d = naturalSlot.components.slot.sqrDistanceFrom(anchor!);
					if(d >= distance) continue;
					distance = d;
					myAnchor = anchor;
				}
			}

			//myAnchor!.emit("attach-to-slot", naturalSlot);
			naturalSlot.setAttribute("slot", { item : this.el, anchor : myAnchor });
		};

		this.detachListener = ((event : DetailEvent<SlotEntity>) =>
		{
			const slotEl = event.detail;
			const slotData = slotEl.components.slot?.data;
			if(slotData?.item !== this.el)
				return; //Not for me.

			event.stopPropagation();
			event.preventDefault();

			if(slotData.locked)
				return; //Slot is locked, can't detach.
	
			//Remove this from slotEl
			slotEl.setAttribute("slot", { item : null, anchor : null } );

			attachToNaturalSlot();
		}) as EventListener;
		this.el.addEventListener("attach-to-slot", this.attachListener);
		this.el.addEventListener("detach-from-slot", this.detachListener);


		// Right now Slot/Slottable is the only way to change parenting at runtime.
		// Commit to this and handle inherited variables change here :
		this.parentChangedListener = ((event : DetailEvent<Entity>) =>
		{
			// Look for first parent with variables in both old and new hierarchy :
			let newVar : VariablesEntity = this.el.parentEl as VariablesEntity;
			while(newVar && !newVar.components.variables)
				newVar = newVar.parentEl as VariablesEntity;

				let oldVar : VariablesEntity = event.detail as VariablesEntity;
			while(oldVar && !oldVar.components.variables)
				oldVar = oldVar.parentEl as VariablesEntity;

			if(newVar === oldVar)
				return; // Variables didn't change. (including both are null)
			
			propagateVariablesChange(this.el);
		}) as EventListener,
		this.el.addEventListener("parent-element-changed", this.parentChangedListener);

		//Initialize by attaching itself to its natural parent slot
		if(this.el.hasLoaded)
			attachToNaturalSlot();
		else
			this.el.addEventListener("loaded", attachToNaturalSlot, { once:true } );
	},

	remove(this : SlottableComponent)
	{
		this.el.removeEventListener("attach-to-slot", this.attachListener);
		this.el.removeEventListener("detach-from-slot", this.detachListener);
		this.el.removeEventListener("parent-element-changed", this.parentChangedListener);
	}
});


